import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from './baseQuery';

export type AdminUser = {
    _id: string;
    fullName: string;
    email: string;
    dateCreated: string;
    tradeAccount: string | null;
    verified: boolean;
    onboardingRequired: boolean;
    onboardingStep: 1 | 2 | 3 | 4 | 5 | 6;
    questionnaireCompleted: boolean;
    questionnaireSubmittedAt: string | null;
    botContactedAt: string | null;
    softwareConfirmedAt: string | null;
    crmFormConfirmedAt: string | null;
    onboardingCompletedAt: string | null;
    passportMainFile: string | null;
    passportRegistrationFile: string | null;
    passportUploadedAt: string | null;
    passportStatus: 'pending' | 'approved' | 'rejected' | null;
    passportRejectionComment: string | null;
};

export type AdminLoginResponse = {
    _id: string;
    email: string;
    fullName: string;
};

export type AdminCheckAuthResponse = {
    isValid: boolean;
    admin?: {
        _id: string;
        email: string;
        fullName: string;
    };
};

export type UserListFilters = {
    onboardingStep?: 1 | 2 | 3 | 4 | 5 | 6;
    passportStatus?: 'pending' | 'approved' | 'rejected' | 'none';
    onboardingRequired?: 'true' | 'false';
};

export type GetUsersParams = UserListFilters & {
    offset: number;
    limit: number;
};

export type GetUsersResponse = {
    users: (AdminUser & { balance?: number })[];
    count: number;
};

export type SearchUsersParams = UserListFilters & {
    fullName?: string;
    email?: string;
};

export type SetOnboardingStepParams = {
    _id: string;
    onboardingStep: 1 | 2 | 3 | 4 | 5 | 6;
};

export type ApprovePassportParams = {
    _id: string;
};

export type RejectPassportParams = {
    _id: string;
    comment: string;
};

export type AdminDivisionStudent = {
    name: string;
    id_TCSubAccount: number | null;
    id_TCUsers: number | null;
    id_capital: number | null;
    email: string | null;
    fullname: string | null;
    capital_name: string | null;
    currentMember: boolean;
    todayPayout: number;
    yesterdayPayout: number;
    weekPayout: number;
    monthPayout: number;
    prevMonthPayout: number;
    yearPayout: number;
    commonPayout: number;
};

export type AdminDivision = {
    id_TCCapital: number;
    name: string;
    id_User: number;
    create_dt: string;
    balance: number;
    students_count: number;
    commonPayout: number;
    monthPayout: number;
    prevMonthPayout: number;
    students: AdminDivisionStudent[];
};

export type GetDivisionsParams = {
    startDate?: string;
    endDate?: string;
    divisionNames?: string;
};

export type DivisionNameItem = {
    name: string;
};

export const adminApi = createApi({
    reducerPath: 'adminApi',
    baseQuery,
    tagTypes: ['admin', 'users', 'user', 'divisions'],
    endpoints: (builder) => ({
        login: builder.mutation<AdminLoginResponse, { email: string; password: string }>({
            query: (body) => ({ url: '/admin/login', method: 'POST', body }),
            invalidatesTags: ['admin'],
        }),
        checkAuth: builder.query<AdminCheckAuthResponse, void>({
            query: () => ({ url: '/admin/auth' }),
            providesTags: ['admin'],
        }),
        signOut: builder.query<{ message: string }, void>({
            query: () => ({ url: '/admin/signout' }),
        }),
        getUsers: builder.query<GetUsersResponse, GetUsersParams>({
            query: (params) => ({ url: '/admin/users/getUsers', params }),
            providesTags: ['users'],
        }),
        searchUsers: builder.query<AdminUser[], SearchUsersParams>({
            query: (params) => ({ url: '/admin/users/searchUsers', params }),
            providesTags: ['users'],
        }),
        getUser: builder.query<AdminUser, string>({
            query: (id) => ({ url: `/admin/users/getUser/${id}` }),
            providesTags: ['user'],
        }),
        setOnboardingStep: builder.mutation<AdminUser, SetOnboardingStepParams>({
            query: (body) => ({ url: '/admin/users/setOnboardingStep', method: 'PATCH', body }),
            invalidatesTags: ['users', 'user'],
        }),
        approvePassport: builder.mutation<AdminUser, ApprovePassportParams>({
            query: (body) => ({ url: '/admin/users/approvePassport', method: 'PATCH', body }),
            invalidatesTags: ['users', 'user'],
        }),
        rejectPassport: builder.mutation<AdminUser, RejectPassportParams>({
            query: (body) => ({ url: '/admin/users/rejectPassport', method: 'PATCH', body }),
            invalidatesTags: ['users', 'user'],
        }),
        getDivisionNames: builder.query<DivisionNameItem[], void>({
            query: () => ({ url: '/admin/division/divisionNames' }),
            providesTags: ['divisions'],
        }),
        getDivisions: builder.query<AdminDivision[], GetDivisionsParams>({
            query: (params) => ({ url: '/admin/division', params }),
            providesTags: ['divisions'],
        }),
    }),
});

export const {
    useLoginMutation,
    useCheckAuthQuery,
    useLazySignOutQuery,
    useGetUsersQuery,
    useSearchUsersQuery,
    useGetUserQuery,
    useSetOnboardingStepMutation,
    useApprovePassportMutation,
    useRejectPassportMutation,
    useGetDivisionNamesQuery,
    useGetDivisionsQuery,
} = adminApi;

export const getPassportUrl = (id: string, side: 'main' | 'registration'): string =>
    `/api/admin/users/getPassportFile/${id}/${side}`;

export const getDivisionDownloadUrl = (params: GetDivisionsParams): string => {
    const query = new URLSearchParams();
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    if (params.divisionNames) query.set('divisionNames', params.divisionNames);

    return `/api/admin/division/download?${query.toString()}`;
};
